import {HttpErrorResponse, HttpResponse} from "@angular/common/http";
import {map} from "rxjs/operators";
import {filter, firstValueFrom, MonoTypeOperatorFunction, Observable, OperatorFunction, takeWhile} from "rxjs";

/** Pakker data som leveres fra datatjenester inn i et objekt som også gir tilgang til
 * metainformasjon om bakenforliggende ressurskall. */
export interface DataOgMeta<T> {
    /** Data mottatt fra server */
    data: T;
    meta: {
        /** Sann dersom data kommer fra cache/lokallager (typisk impl vjha Elf), usann dersom data kommer fra request. */
        levertFraCache: boolean;
        /** Sann dersom http requesten returnerte med 2xx responskode, kun relevant dersom levertFraCache = false */
        httpResponsStatusOk: boolean;
        /** Responsstatus til http requesten, kun relevant dersom levertFraCache = false */
        httpResponsStatus: number;
    };
}

/** Returnerer en DataOgMeta instans som representerer en feilet request, merk at {@link DataOgMeta#data} kan være null */
export function dataOgMetaForFeiletRequest<T>(feil: HttpErrorResponse, data: T | null): DataOgMeta<T> {
    return {
        data: data!,
        meta: {
            levertFraCache: false,
            httpResponsStatus: feil.status,
            httpResponsStatusOk: false
        }
    }
}

/** Returnerer en DataOgMeta instans som representerer en OK request. */
export function dataOgMetaForOkRequest<T>(data: T, respons: HttpResponse<any> | null): DataOgMeta<T> {
    const httpStatus = respons?.status ?? 200;
    return {
        data: data,
        meta: {
            levertFraCache: respons == null,
            httpResponsStatus: httpStatus,
            httpResponsStatusOk: Math.floor(httpStatus / 100) == 2 //alle 2xx responser anses ok
        }
    }
}

/** Returnerer en DataOgMeta instans som representerer data hentet fra cache/lokalt datalager. */
export function dataOgMetaForCachetSvar<T>(data: T): DataOgMeta<T> {
    return {
        data: data,
        meta: {
            levertFraCache: true,
            httpResponsStatus: 200,
            httpResponsStatusOk: true
        }
    }
}

/** Funksjon for bruk ifbm rxjs observable, DataOgMeta -> data */
export function somData<T>(): (dataOgMeta: DataOgMeta<T>) => T {
    return (dataOgMeta: DataOgMeta<T>): T => dataOgMeta.data;
}

/**********************************************************************
 * hjelpemetoder for pipe
 **********************************************************************/

/** Sann dersom angitt dataOgMeta representerer respons fra server og ikke data hentet fra cache */
export function erLevertFraServer<T>(dataOgMeta: DataOgMeta<T>): boolean {
    return !dataOgMeta.meta.levertFraCache;
}

/** Rxjs pipe som terminerer subscription når første verdi med
 * {@link DataOgMeta.meta.levertFraCache} == false har blitt levert */
export function terminerEtterFørsteRequestRespons<T>(): MonoTypeOperatorFunction<DataOgMeta<T>> {
    let levertFraRequest = false;
    return takeWhile((verdi: DataOgMeta<T>) => {
        if (levertFraRequest) return false;
        levertFraRequest = !verdi.meta.levertFraCache;
        return true;
    });
}

/** Rxjs pipe som filtrerer bort leveranser som kommer fra cache. */
export function kunRequestRespons<T>(): MonoTypeOperatorFunction<DataOgMeta<T>> {
    return filter((verdi: DataOgMeta<T>) => !verdi.meta.levertFraCache);
}

/** Funksjon for bruk ifbm rxjs observable, DataOgMeta -> data */
export function mapTilData<T>(): OperatorFunction<DataOgMeta<T>, T> {
    return map(somData());
}

/** Returnerer {@link DataOgMeta#data} fra første verdi levert fra server for angitt observerbar  */
export function førsteVerdiLevertFraServer<T>(dataOgMeta: Observable<DataOgMeta<T>>): Promise<T> {
    return firstValueFrom(dataOgMeta.pipe(kunRequestRespons(), mapTilData()));
}
